import { useEffect, useId, useMemo, useRef, useState } from 'react';
import type { KeyboardEvent } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { cn } from '@shared/lib';
import type { Word } from '@shared/types';
import { PlainField } from '@shared/ui';
import { MAX_SESSION_CARDS as MAX_CARDS } from '../model/builderConfig';
import { cardShows, sessionTranslations, takeUpTo, totalWeight } from '../model/cardWeight';
import './CardPicker.css';

// Selection uses user_card_state.id: different meanings may share a card_id.
export type ReadyWord = Word & { stateId: string };

interface CardPickerProps {
  cards: ReadyWord[];
  pool: Set<string>;
  mistakeCounts?: Map<string, number>;
  onApply: (pool: Set<string>) => void;
  onClose: () => void;
}

/** Changes stay local until Apply; closing the dialog discards the draft. */
export function CardPicker({ cards, pool, mistakeCounts, onApply, onClose }: CardPickerProps) {
  const { t } = useTranslation();
  const dialogRef = useRef<HTMLDialogElement>(null);
  const headingRef = useRef<HTMLHeadingElement>(null);
  const titleId = useId();
  const descriptionId = useId();
  const statusId = useId();
  const [query, setQuery] = useState('');
  // Строка — слово в части речи; карточек у неё по числу отмеченных смыслов (cluster:
  // «гроздь», «группа»). Счётчик и лимит — в карточках, как они созданы (2026-10-06).
  const shows = useMemo(() => new Map(cards.map((card) => [card.stateId, cardShows(card)])), [cards]);
  const weight = (id: string) => shows.get(id) ?? 1;
  const translations = useMemo(
    () => new Map(cards.map((card) => [card.stateId, sessionTranslations(card)])),
    [cards],
  );
  const [draft, setDraft] = useState(
    () =>
      new Set(
        takeUpTo(
          cards.filter((card) => pool.has(card.stateId)).map((card) => card.stateId),
          MAX_CARDS,
          (id) => shows.get(id) ?? 1,
        ),
      ),
  );
  const selected = useMemo(
    () => new Set(cards.filter((card) => draft.has(card.stateId)).map((card) => card.stateId)),
    [cards, draft],
  );
  const selectedCards = totalWeight(selected, weight);
  const totalCards = totalWeight(shows.keys(), weight);
  const search = query.trim().toLocaleLowerCase();
  const rows = useMemo(
    () =>
      cards.filter(
        (card) =>
          !search ||
          `${card.word} ${(translations.get(card.stateId) ?? []).join(' ')}`.toLocaleLowerCase().includes(search),
      ),
    [cards, search, translations],
  );
  const orderMap = useMemo(() => new Map(cards.map((card, index) => [card.stateId, index + 1])), [cards]);
  const atLimit = selectedCards >= MAX_CARDS;
  // Строка не влезает, если с ней карточек станет больше лимита.
  const blocked = (id: string) => !selected.has(id) && selectedCards + weight(id) > MAX_CARDS;

  useEffect(() => {
    const dialog = dialogRef.current;
    const previousFocus = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    const previousOverflow = document.body.style.overflow;
    if (!dialog) return;

    // Native modal semantics also make the background inert for assistive tech.
    dialog.showModal();
    headingRef.current?.focus();
    document.body.style.overflow = 'hidden';
    return () => {
      dialog.close();
      document.body.style.overflow = previousOverflow;
      if (previousFocus?.isConnected) previousFocus.focus({ preventScroll: true });
    };
  }, []);

  const trapFocus = (event: KeyboardEvent<HTMLDialogElement>) => {
    if (event.key !== 'Tab') return;
    const controls = Array.from(
      event.currentTarget.querySelectorAll<HTMLElement>(
        'button:not(:disabled), input:not(:disabled), [contenteditable="true"], [tabindex="0"]',
      ),
    );
    const first = controls[0];
    const last = controls[controls.length - 1];
    const active = document.activeElement;
    if (event.shiftKey && (active === first || active === headingRef.current)) {
      event.preventDefault();
      last?.focus();
    } else if (!event.shiftKey && active === last) {
      event.preventDefault();
      first?.focus();
    }
  };

  const toggleCard = (id: string) => {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id);
    else if (!blocked(id)) next.add(id);
    setDraft(next);
  };

  const selectVisible = () => {
    // Search adds matches to the draft; without search, choose the whole module
    // or its first 100 cards, matching the button's explicit label.
    const kept = search ? [...selected] : [];
    const keptSet = new Set(kept);
    const room = MAX_CARDS - totalWeight(kept, weight);
    const added = takeUpTo(
      rows.map((card) => card.stateId).filter((id) => !keptSet.has(id)),
      room,
      weight,
    );
    setDraft(new Set([...kept, ...added]));
  };

  const status = atLimit
    ? t('cram.builder.picker.limitReached', {
        max: MAX_CARDS,
        defaultValue: 'Выбрано {{max}} карточек. Сними одну, чтобы добавить другую.',
      })
    : selected.size === 0
      ? t('cram.builder.picker.selectAtLeastOne', 'Выбери хотя бы одну карточку.')
      : t('cram.builder.picker.limit', {
          max: MAX_CARDS,
          defaultValue: 'Можно выбрать до {{max}} карточек за сессию.',
        });

  return createPortal(
    <dialog
      ref={dialogRef}
      className="bld-pk bld-card-picker"
      aria-labelledby={titleId}
      aria-describedby={descriptionId}
      aria-modal="true"
      onKeyDown={trapFocus}
      onCancel={(event) => {
        event.preventDefault();
        onClose();
      }}
      onClick={(event) => {
        if (event.target !== event.currentTarget) return;
        const bounds = event.currentTarget.getBoundingClientRect();
        if (
          event.clientX < bounds.left ||
          event.clientX > bounds.right ||
          event.clientY < bounds.top ||
          event.clientY > bounds.bottom
        )
          onClose();
      }}
    >
      <div className="bld-pk-head">
        <div className="bld-pk-htop">
          <div>
            <h2 id={titleId} ref={headingRef} tabIndex={-1} className="bld-pk-title">
              {t('cram.builder.picker.title')}
            </h2>
            <p id={descriptionId} className="bld-pk-sub">
              {t('cram.builder.picker.sub', { max: MAX_CARDS })}
            </p>
          </div>
          <button type="button" className="bld-pk-x" onClick={onClose} aria-label={t('cram.builder.picker.close')}>
            <svg
              aria-hidden="true"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.2"
              strokeLinecap="round"
            >
              <path d="M6 6l12 12M18 6L6 18" />
            </svg>
          </button>
        </div>
        <div className="bld-pk-search">
          <svg
            aria-hidden="true"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
          >
            <circle cx="11" cy="11" r="7" />
            <path d="M21 21l-4-4" />
          </svg>
          <PlainField
            value={query}
            onChange={setQuery}
            placeholder={t('cram.builder.picker.search')}
            ariaLabel={t('cram.builder.picker.search')}
          />
        </div>
        <div className="bld-pk-presets">
          <button
            type="button"
            onClick={selectVisible}
            disabled={
              !rows.length ||
              (Boolean(search) && (atLimit || rows.every((card) => selected.has(card.stateId) || blocked(card.stateId))))
            }
          >
            {search
              ? t('cram.builder.picker.selectVisible', 'Выбрать найденные')
              : totalCards > MAX_CARDS
                ? t('cram.builder.picker.presetUpTo', { n: MAX_CARDS, defaultValue: 'Первые {{n}}' })
                : t('cram.builder.picker.presetAll')}
          </button>
          <button type="button" className="danger" onClick={() => setDraft(new Set())} disabled={selected.size === 0}>
            {t('cram.builder.picker.presetNone')}
          </button>
        </div>
      </div>

      <div className="bld-pk-list">
        {rows.length === 0 ? (
          <p className="bld-pk-empty" role="status">
            {t('cram.builder.picker.empty')}
          </p>
        ) : (
          rows.map((card) => {
            const checked = selected.has(card.stateId);
            const disabled = blocked(card.stateId);
            const count = weight(card.stateId);
            const mistakeCount = mistakeCounts?.get(card.stateId) ?? 0;
            return (
              <label
                key={card.stateId}
                className={cn(
                  'bld-pk-row',
                  checked && 'on',
                  disabled && 'at-limit',
                  mistakeCount > 0 && 'has-mistakes',
                )}
              >
                <input
                  className="bld-pk-checkbox"
                  type="checkbox"
                  checked={checked}
                  disabled={disabled}
                  onChange={() => toggleCard(card.stateId)}
                  aria-describedby={disabled ? statusId : undefined}
                />
                <span className="bld-pk-box" aria-hidden="true">
                  <svg
                    width="13"
                    height="13"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="3.4"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M20 6 9 17l-5-5" />
                  </svg>
                </span>
                <span className="bld-pk-pw">
                  <span className="bld-pk-en">{card.word}</span>
                  <span className="bld-pk-ru">
                    {(translations.get(card.stateId) ?? [card.ru]).join(', ')}
                    {count > 1 && (
                      <span className="bld-pk-count">{t('training.cardCount', { count })}</span>
                    )}
                  </span>
                  {mistakeCount > 0 && (
                    <span className="bld-pk-mistakes">
                      {t('cram.builder.picker.mistakes', {
                        count: mistakeCount,
                        defaultValue: 'Ошибок: {{count}}',
                      })}
                    </span>
                  )}
                </span>
                <span className="bld-pk-idx" aria-hidden="true">
                  {String(orderMap.get(card.stateId)).padStart(2, '0')}
                </span>
              </label>
            );
          })
        )}
      </div>

      <div className="bld-pk-feedback">
        <p id={statusId} className={cn('bld-pk-status', atLimit && 'at-limit')} role="status">
          {status}
        </p>
      </div>
      <div className="bld-pk-foot">
        <span className="bld-pk-fcount" aria-live="polite" aria-atomic="true">
          <b>{selectedCards}</b> {t('cram.builder.picker.ofTotal', { total: totalCards })}
        </span>
        <div className="bld-pk-actions">
          <button type="button" className="bld-pk-cancel" onClick={onClose}>
            {t('cram.builder.picker.cancel', 'Отмена')}
          </button>
          <button
            type="button"
            className="bld-pk-done"
            disabled={selected.size === 0}
            onClick={() => {
              onApply(new Set(selected));
              onClose();
            }}
          >
            {t('cram.builder.picker.apply', 'Применить')}
          </button>
        </div>
      </div>
    </dialog>,
    document.body,
  );
}
